/**
 * The tiling engine (spec §6).
 *
 * A stay is a timeline of N nights. Pieces tile it. The guest pays the cheapest
 * valid tiling. Operators declare pieces; they never declare precedence.
 *
 * Implemented as a labelled DP rather than QPC's partition container, so the
 * dominance rule is provable instead of delicate — see `labelKey` and §6.1.
 *
 * Pure: no I/O, no clock, no randomness. Safe to call in a render pass and to
 * run inside a Worker.
 */

import type {
  AppliedModifier, Money, Piece, PieceSet, PriceRequest, Quantities, Quote, QuoteLine,
  QuoteMessage, RoundingMode,
} from './types';
import { applyRounding, evaluatePrice, normaliseQuantities, strongestRounding } from './price';
import { checkOccupancy, computeExtras } from './fees';
import {
  placementsFor, isDynamic, weekdayAllowed, type KindContext, type Placement, type PricedPiece,
} from './kinds';

/**
 * Cheapest-label cap for constrained labels (spec §6.1).
 *
 * Set to 1 to reproduce QPC exactly (it collapses these to one, which is
 * unsound against a lower bound and can miss a cheaper valid tiling — see
 * test/divergences.json). The whole fixture suite is 62/62 at 1, and 61 + one
 * documented divergence at 8.
 */
const K_CONSTRAINED_DEFAULT = 8;
/**
 * Blow-up guard: 512 distinct labels per night.
 *
 * NOT the "fail loudly" the name suggests, outside `strict`: `insert` returns
 * false, the label is dropped, and the DP answers from what survived. And this
 * IS reached on production data — the 2026 `kamperen/comfort` card carries six
 * `continueWhenBroken` rates whose step vectors exceed 512 states per night on a
 * 21-night stay. `PriceStayOptions.strict` describes the non-strict path as
 * "returning unavailable", which it does not do either.
 *
 * THAT REASONING WAS WRONG, and the correction matters more than the original
 * note. It said every case tried gave the same total truncated as exhaustive.
 * The cases tried stopped at 21 nights; past that the same card diverges, and
 * the divergence is an OVERCHARGE, because a dropped label is a cheaper tiling
 * the search never gets to compare. Measured against this engine with the cap
 * lifted to 200000, on the park's 2027 camping card:
 *
 *     21 nights   quoted 1245   cheapest legal 1220    +2.0%
 *     28 nights   quoted 1785   cheapest legal 1625    +9.8%
 *     90 nights   quoted 5179   cheapest legal 3498   +32.0%
 *
 * The cap is no longer reached on that card, because the states it was full of
 * should never have existed: `stepsThatMatter` keeps a piece's expansion
 * counter in a label's identity only where the count can change the future. The
 * card now prices 61 nights in 63 ms rather than 18.8 seconds, and agrees with
 * the uncapped engine on every stay tried.
 *
 * The cap stays as a backstop for a set nobody has built yet. If it is ever
 * reached again, treat it as a REPORT rather than a limit: something in that
 * card is carrying state it does not need, and dropping labels will misprice
 * it quietly.
 */
const MAX_LABELS_PER_NIGHT = 512;

interface Trail {
  piece: Piece;
  startNight: number;
  length: number;
  price: Money;
  prev: Trail | null;
}

interface Label {
  cost: Money;
  uses: Readonly<Record<string, number>>;
  /** Expansion UNITS consumed per `continueWhenBroken` piece (spec §2.3.1). */
  steps: Readonly<Record<string, number>>;
  trail: Trail | null;
}

/** Stable `k:v,` rendering of a small integer record, for the label key. */
function recordKey(
  rec: Readonly<Record<string, number>>, only?: ReadonlySet<string>,
): string {
  const keys = only ? Object.keys(rec).filter((k) => only.has(k)) : Object.keys(rec);
  if (keys.length === 0) return '';
  keys.sort();
  let acc = '';
  for (const k of keys) acc += `${k}:${rec[k]},`;
  return acc;
}

/**
 * Label identity.
 *
 * Two labels are interchangeable only when their FUTURE is identical: same
 * usage headroom, same outstanding minimum. QPC normalises a satisfied minimum
 * to zero (partition_can_compare) so labels that have already cleared their
 * floor rejoin the unconstrained bucket; we do the same.
 */
/**
 * Two labels are interchangeable only when their FUTURE is identical: same usage
 * headroom, and the same answer to "has the floor already been cleared?".
 * With a single uniform floor per level that second component is one bit,
 * instead of the per-piece maximum the old design carried.
 */
function labelKey(
  cost: Money,
  requiredMin: Money,
  uses: Readonly<Record<string, number>>,
  steps: Readonly<Record<string, number>>,
  countedSteps: ReadonlySet<string>,
): string {
  const cleared = cost >= requiredMin ? '1' : '0';
  // Two labels with different expansion progress are NOT interchangeable WHEN
  // that progress changes the future: the marginal cost of the next step
  // differs under a decay, and the headroom differs under a cap. When it
  // changes nothing, they have identical futures and belong in one bucket.
  // See `stepsThatMatter`.
  return `${recordKey(uses)}|${recordKey(steps, countedSteps)}|${cleared}`;
}

/**
 * Which pieces' expansion counters belong in a label's identity.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS FIXES, AND IT IS NOT ONLY SPEED
 * ---------------------------------------------------------------------------
 *
 * A label's key answers one question: do two prefixes have the same FUTURE? A
 * step count changes the future in exactly three ways, and each is a field on
 * the extension — `decay` (the next step's price depends on how many came
 * before), `maxSteps` (how many remain), `minSteps` (how many must still be
 * taken). With none of the three, the next step costs the same and is equally
 * legal whatever the count, so two labels differing only in that number are
 * interchangeable.
 *
 * Keeping them apart was not merely wasteful. A counter with a cap has a small
 * range — this park's rental cards cap theirs at four — but one with neither a
 * cap nor a decay is unbounded, so each multiplies the state space by
 * O(nights). The camping card has five. It hit `MAX_LABELS_PER_NIGHT` and,
 * on the non-strict path, labels were SILENTLY DROPPED: the search returned the
 * cheapest tiling it had room to find rather than the cheapest that exists.
 *
 * That is an overcharge, not a slowdown. Measured against the same engine with
 * the cap lifted to 200000, on the park's own 2027 cards:
 *
 *     21 nights   quoted 1245   cheapest legal 1220   +2.0%
 *     28 nights   quoted 1785   cheapest legal 1625   +9.8%
 *     90 nights   quoted 5179   cheapest legal 3498  +32.0%
 *
 * With the counters that cannot matter removed, the engine returns the
 * uncapped answer — exactly, on every case tried — and does it in 52 ms at 61
 * nights where it took 18.8 seconds, and 206 ms at 180 where it took 113.
 *
 * The comment on `MAX_LABELS_PER_NIGHT` said every case tried gave the same
 * total truncated as exhaustive. That was true of the cases tried, which
 * stopped at 21 nights on the 2026 card; it is not true of this one.
 */
function stepsThatMatter(set: PieceSet): ReadonlySet<string> {
  const out = new Set<string>();
  for (const piece of set.pieces) {
    const extension = (piece as { params?: { extension?: {
      decay?: { factor?: number; startFactor?: number; endPrice?: unknown };
      maxSteps?: number; minSteps?: number;
    } } }).params?.extension;
    if (!extension) continue;
    if (extension.maxSteps !== undefined || extension.minSteps !== undefined) {
      out.add(piece.id);
      continue;
    }
    /**
     * A decay that does not decay is not a decay.
     *
     * The legacy reader attaches a `decay` object to EVERY extendable piece,
     * neutral or not — `factor: 1, startFactor: 1`, no end price — because the
     * fields exist on every QPC record. Testing for the object's presence
     * therefore made every legacy piece's counter significant while the same
     * card lifted to a programme had none, and the two priced differently: one
     * search exact, the other still exploding into the label cap. It is the
     * same question either way, so it gets the same answer.
     */
    const decay = extension.decay;
    if (decay !== undefined
      && ((decay.factor ?? 1) !== 1
        || (decay.startFactor ?? 1) !== 1
        || decay.endPrice !== undefined)) {
      out.add(piece.id);
    }
  }
  return out;
}

export interface PriceStayOptions {
  /** Use the older labelled-floor DP instead of floor-level decomposition. */
  legacyLabelledFloors?: boolean;
  /** Throw instead of returning `unavailable` when the label cap trips. */
  strict?: boolean;
  /**
   * Reproduce QPC's (unsound) collapse of constrained labels to one.
   * For differential testing against the legacy engine only — never in production.
   */
  qpcCompatibleMinTotal?: boolean;
}

/**
 * Price a stay at the cheapest legal total.
 *
 * FLOOR-LEVEL DECOMPOSITION (credit: the independent benchmark solver, which
 * found this from a cold start and it is cleaner than carrying the floor in the
 * DP label).
 *
 * `minStayTotal` is a lower bound on the FINAL total, which breaks cost-based
 * dominance: a cheaper prefix can be infeasible where a costlier one is not.
 * Carrying it as label state needs a k-cheapest heuristic and a cap, and is
 * only sound within k.
 *
 * Instead, enumerate the distinct floor levels F present. At each level, admit
 * only pieces whose floor is <= F and require the total to be >= F. Then:
 *
 *   - every tiling produced is legal, since each piece's floor <= F <= total;
 *   - the true optimum T appears at F* = max floor among T's pieces, because
 *     all of T's pieces are admitted there and T's cost >= F* (T being legal);
 *   - so min over levels is EXACT.
 *
 * And at each level the floor vanishes from the label entirely — it is a single
 * check on the final answer. The DP is left carrying only `uses`, where
 * dominance IS sound.
 *
 * The residual hard case is a level whose plain cheapest tiling falls below F.
 * That still needs a "cheapest total >= F" search, and is where the labelled
 * path is retained as a fallback — but it is now isolated rather than paid on
 * every query.
 */
export function priceStay(
  set: PieceSet,
  req: PriceRequest,
  options: PriceStayOptions = {},
): Quote {
  if (options.legacyLabelledFloors) return priceStayInternal(set, req, options);

  const qty = normaliseQuantities(req.quantities, Math.max(req.nights, 1));

  // Whether the party fits is a fact about the booking, not about the rate
  // card, so it is settled before the search. Reporting "no combination of
  // tariffs covers this period" for a party of eight in a four-berth lodge
  // would send the operator hunting through the rates for a problem that is
  // nothing to do with them.
  if (req.nights >= 1 && Number.isInteger(req.nights)) {
    const problem = checkOccupancy(set, qty, req.nights);
    if (problem) {
      return {
        status: 'unavailable',
        arrival: req.arrival,
        nights: req.nights,
        lines: [],
        extras: [],
        extrasTotal: 0,
        accommodationTotal: 0,
        subtotal: 0,
        total: 0,
        appliedModifiers: [],
        messages: [],
        reason: problem.message,
      };
    }
  }

  // Evaluate each floor ONCE. The 2026 camping card has 36 floored rates and 24
  // distinct levels, and re-evaluating every floor inside every level made this
  // quadratic in the number of floored rates for no reason.
  const floorOf = new Map<string, number>();
  const levels = new Set<number>([0]);
  for (const piece of set.pieces) {
    if (!piece.minStayTotal) continue;
    const f = evaluatePrice(piece.minStayTotal, qty, req.nights, 0);
    floorOf.set(piece.id, f);
    levels.add(f);
  }

  // Per-stay piece prices do not depend on the level, only on the stay. Sharing
  // one cache across every level turns 24 full precomputes into one.
  const shared: SharedPrices = { priced: new Map(), key: `${req.arrival}:${req.nights}` };

  let best: Quote | null = null;
  for (const level of [...levels].sort((a, b) => a - b)) {
    // EARLY EXIT. Levels are ascending and every answer at level F satisfies
    // SUBTOTAL >= F, so once F reaches the best subtotal already found, no
    // remaining level can beat it. Exact, not a heuristic — and on the camping
    // card it removes most of the 24 DP runs.
    //
    // Against `best.total` it is NOT exact. A level bounds the subtotal, while
    // `total` is the subtotal after the modifiers and the rounding, and a
    // modifier may scale it DOWN: with a x0.5 discount the loop stopped at the
    // first level >= half the incumbent subtotal and never ran the level that
    // held the cheaper tiling. Worse, that made the quote depend on the
    // incumbent, so ADDING a cheap floor-free rate raised the price — it
    // lowered `best.total`, the loop exited sooner, and a floored rate that
    // would have won was never admitted.
    if (best !== null && level >= best.subtotal - 1e-9) break;

    const admitted = set.pieces.filter((p) => (floorOf.get(p.id) ?? 0) <= level + 1e-9);
    if (admitted.length === 0) continue;

    // Within a level every admitted floor is <= level, so the label needs no
    // floor dimension: stripping minStayTotal is safe and is the whole point.
    const stripped: PieceSet = {
      ...set,
      pieces: admitted.map((p) => (p.minStayTotal ? { ...p, minStayTotal: undefined } : p)),
    };
    // Plain cheapest at this level — no floor in the label at all.
    let q = priceStayInternal(stripped, req, options, 0, shared);
    // An overflow is not "no tiling at this level", it is "this level's answer
    // is not to be trusted". Skipping it would let a NEIGHBOURING level supply
    // a number as though nothing had happened.
    if (q.status === 'unavailable' && q.lines.length === 0) continue;

    if (q.subtotal < level - 1e-9) {
      // Residual case: the cheapest tiling here does not clear its own floor,
      // so the answer at this level (if one exists) is a DELIBERATELY costlier
      // tiling. This is the only place the labelled search is needed, and now
      // it carries a single uniform floor rather than a per-piece maximum.
      q = priceStayInternal(stripped, req, options, level, shared);
        if (q.status === 'unavailable' && q.lines.length === 0) continue;
      if (q.subtotal < level - 1e-9) continue;
    }
    if (best === null || q.total < best.total) best = q;
  }

  if (best) return withExtras(set, best, qty, req);

  // No level yielded a legal tiling, so there is none.
  //
  // This used to fall back to `priceStayInternal(set, ...)` with floors ignored.
  // That fallback can ONLY ever return an illegal answer: level 0 already
  // admits every floor-free piece, so any floor-free tiling has been found
  // above, and anything the fallback adds is precisely a tiling that violates a
  // floor. It quoted €351.14 on a stay whose only usable piece carries a €365
  // minimum — below the floor, and cheaper than the real answer, which is the
  // exact shape of a false "the site overcharges" finding.
  //
  // Unpriceable must return unpriceable.
  return {
    status: 'unavailable',
    arrival: req.arrival,
    nights: req.nights,
    lines: [],
    extras: [],
    extrasTotal: 0,
    accommodationTotal: 0,
    subtotal: 0,
    total: 0,
    appliedModifiers: [],
    messages: [],
    reason: 'no tiling satisfies every applicable minimum booking price',
  };
}

/**
 * Add what is owed on top of the winning tiling.
 *
 * After the search and after the modifiers, deliberately. A tourist tax is not
 * multiplied by a peak-season factor, and it is not an alternative way to cover
 * a night that the search could decline to take.
 */
function withExtras(
  set: PieceSet, quote: Quote, qty: Quantities, req: PriceRequest,
): Quote {
  if (quote.status === 'unavailable') return quote;
  const { extras, total } = computeExtras(set, qty, req.arrival, req.nights);
  if (extras.length === 0) return quote;
  return {
    ...quote,
    extras,
    extrasTotal: total,
    total: Math.round((quote.accommodationTotal + total) * 100) / 100,
  };
}

/*
 * REMOVED: priceLosRow() — a single-DP-run LOS row.
 *
 * The idea is tempting and wrong. `frontier[d]` looks like it already holds the
 * cheapest tiling of every prefix, so one run at N=max should yield the whole
 * row. It does not, for two compounding reasons:
 *
 *  1. A piece may be TRIMMED to land on the departure and still costs its full
 *     price. Trimming depends on N, so frontier[n] under N=max differs from a
 *     dedicated run at n. A closing pass over the last piece fixes this much.
 *
 *  2. Envelope checks are evaluated against the MAXIMAL placement length —
 *     `weekdayAllowed(day + length, departureWeekdays)` in particular. A
 *     weekday-constrained piece rejected at its maximal length never gets to
 *     offer its shorter, legal trims, so the closing pass cannot recover it.
 *     Each candidate closing length needs its own departure/window validation.
 *
 * Measured 6.6x on a 183 x 30 grid but disagreed with per-length priceStay on
 * 47 of 140 sampled cells, in BOTH directions. It is not worth shipping a fast
 * grid that is quietly wrong; the materialiser is Phase D work and deserves its
 * own correctness harness (compare every cell against priceStay before any
 * speedup is believed).
 *
 * The honest speedup available today is the per-stay price precompute, which is
 * covered by the fixture suite: ~40% off a 28-night call.
 */

/**
 * Availability precedence across the pieces of one tiling.
 *
 * A quote's status is a property of the SET of pieces used, not of the last one
 * the trail happens to end on, so it is folded rather than assigned. Refusing
 * beats on-request beats bookable: a stay containing something the park will
 * not take is not made takeable by something else asking for a phone call.
 */
const STATUS_RANK: Record<Quote['status'], number> = {
  bookable: 0, onRequest: 1, unavailable: 2,
};

function strongest(a: Quote['status'], b: Quote['status']): Quote['status'] {
  return STATUS_RANK[b] > STATUS_RANK[a] ? b : a;
}

function eligibleForStay(piece: Piece, arrival: number, nights: number): boolean {
  if (piece.minStayNights != null && nights < piece.minStayNights) return false;
  if (piece.maxStayNights != null && nights > piece.maxStayNights) return false;
  // Changeover days are a fact about the BOOKING, so they are checked once
  // against the stay's own arrival and departure rather than per placement.
  if (!weekdayAllowed(arrival, piece.stayArrivalWeekdays)) return false;
  if (!weekdayAllowed(arrival + nights, piece.stayDepartureWeekdays)) return false;
  if (piece.stayWindow) {
    if (arrival < piece.stayWindow.start) return false;
    if (arrival + nights > piece.stayWindow.end) return false;
  }
  return !(arrival + nights <= piece.validFrom || arrival >= piece.validTo);
}

/** Per-stay price cache shared across floor levels (see priceStay). */
interface SharedPrices {
  priced: Map<string, PricedPiece>;
  /** Identifies the stay the cache belongs to, so it can never leak across one. */
  key: string;
}

function priceStayInternal(
  set: PieceSet,
  req: PriceRequest,
  options: PriceStayOptions,
  requiredMin: Money = 0,
  shared?: SharedPrices,
): Quote {
  const N = req.nights;
  const arrival = req.arrival;

  const empty = (reason: string): Quote => ({
    status: 'unavailable', arrival, nights: N, lines: [],
    extras: [], extrasTotal: 0, accommodationTotal: 0,
    subtotal: 0, total: 0, appliedModifiers: [], messages: [], reason,
  });

  // `N < 1` alone is not enough: NaN fails every comparison, so a NaN slipped
  // past this guard, `Array.from({length: NaN + 1})` produced an EMPTY array,
  // and the next line dereferenced frontier[0] and threw. A pricing engine
  // handed rubbish must say "unavailable", never crash the request.
  if (!Number.isFinite(N) || !Number.isInteger(N) || N < 1) {
    return empty('stay must be a whole number of nights, at least one');
  }
  if (!Number.isFinite(arrival) || !Number.isInteger(arrival)) {
    return empty('arrival must be a whole number of days');
  }

  const kConstrained = options.qpcCompatibleMinTotal ? 1 : K_CONSTRAINED_DEFAULT;
  const qty = normaliseQuantities(req.quantities, N);
  const ctx: KindContext = { arrival, nights: N, qty };

  // --- Step 1: eligibility (whole-stay constraints, evaluated once) --------
  const candidates: Piece[] = [];
  for (const piece of set.pieces) {
    if (eligibleForStay(piece, arrival, N)) candidates.push(piece);
  }
  if (candidates.length === 0) return empty('no tariff applies to this period');

  // --- Step 2: precompute prices ONCE per stay ----------------------------
  // A piece's price does not depend on where it is placed (QPC calls these
  // "sessions"), so evaluating inside the night loop was pure waste.
  const expectedKey = `${arrival}:${N}`;
  const priced = shared && shared.key === expectedKey ? shared.priced : new Map<string, PricedPiece>();
  for (const piece of candidates) {
    if (priced.has(piece.id)) continue;
    const ext = piece.kind === 'extendable' ? piece.params.extension : undefined;
    const dynamic = isDynamic(piece.price) || isDynamic(ext?.price)
      || isDynamic(ext?.decay?.endPrice);
    priced.set(piece.id, {
      main: evaluatePrice(piece.price, qty, piece.kind === 'closure' ? 1 : piece.params.nights, 0),
      step: ext ? evaluatePrice(ext.price, qty, ext.nights, 0) : 0,
      end: ext?.decay?.endPrice
        ? evaluatePrice(ext.decay.endPrice, qty, ext.nights, 0) : undefined,
      dynamic,
    });
  }
  ctx.priced = priced;

  // --- Step 3: which pieces may START on each night ------------------------
  const startableAt: Piece[][] = Array.from({ length: N }, () => []);
  for (let d = 0; d < N; d++) {
    const day = arrival + d;
    for (const piece of candidates) {
      if (day >= piece.validFrom && day < piece.validTo) startableAt[d].push(piece);
    }
  }

  // --- Steps 4-5: the labelled DP ------------------------------------------
  const countedSteps = stepsThatMatter(set);
  const frontier: Array<Map<string, Label[]>> = Array.from({ length: N + 1 }, () => new Map());
  frontier[0].set(labelKey(0, requiredMin, {}, {}, countedSteps),
    [{ cost: 0, uses: {}, steps: {}, trail: null }]);

  /** Incumbent best complete cost — QPC's branch-and-bound bound (spec §2.6.1). */
  let incumbent = Infinity;

  for (let d = 0; d < N; d++) {
    const here = frontier[d];
    if (here.size === 0) continue; // unreachable night

    for (const labels of here.values()) {
      for (const label of labels) {
        // Branch-and-bound: a prefix already costing more than a complete
        // answer cannot lead anywhere better (QPC does the same, spec 2.6.1).
        if (label.cost >= incumbent) continue;

        for (const piece of startableAt[d]) {
          const used = label.uses[piece.id] ?? 0;
          if (piece.maxPlacements != null && used >= piece.maxPlacements) continue;

          let places: Placement[];
          try {
            // Per-label, not per-night: a `continueWhenBroken` piece prices its
            // next segment from the steps this particular prefix consumed.
            ctx.stepOffset = label.steps;
            places = placementsFor(piece, d, ctx);
          } catch (err) {
            if (options.strict) throw err;
            continue;
          }

          for (const pl of places) {
            const next = d + pl.length;
            if (pl.length < 1 || next > N) continue;

            const cost = label.cost + pl.price;
            let uses = label.uses;
            if (piece.maxPlacements != null) {
              uses = { ...label.uses, [piece.id]: used + 1 };
            }
            let steps = label.steps;
            if (pl.units !== undefined && pl.units !== (label.steps[piece.id] ?? 0)) {
              steps = { ...label.steps, [piece.id]: pl.units };
            }

            const candidate: Label = {
              cost,
              uses,
              steps,
              trail: {
                piece, startNight: d, length: pl.length, price: pl.price, prev: label.trail,
              },
            };
            if (!insert(frontier[next], candidate, kConstrained, requiredMin, countedSteps)) {
              if (options.strict) {
                throw new Error(
                  `tariff set is pathological: more than ${MAX_LABELS_PER_NIGHT} distinct ` +
                  `states at night ${next}`,
                );
              }
            }
            if (next === N && cost >= requiredMin && cost < incumbent) incumbent = cost;
          }
        }
      }
    }
  }

  // --- Step 6: select ------------------------------------------------------
  //
  // A whole-LOS-row collection path used to live here, feeding the removed
  // `priceLosRow`. Nothing has called it since that was withdrawn, so it was
  // ~45 lines of unreachable code that still read as load-bearing — including a
  // "closing pass" whose comment explained a subtlety the live code no longer
  // depends on. LOS rows are produced by @pms/rates, one `priceStay` per cell,
  // which is the only way the trimming rule stays correct.
  let best: Label | null = null;
  for (const labels of frontier[N].values()) {
    for (const label of labels) {
      if (label.cost < requiredMin) continue; // floor not cleared -> invalid tiling
      if (best === null || label.cost < best.cost) best = label;
    }
  }
  if (best === null) return empty('no valid combination of tariffs covers this period');

  // --- Step 7: build the quote --------------------------------------------
  const lines: QuoteLine[] = [];
  const messages: QuoteMessage[] = [];
  let status: Quote['status'] = 'bookable';

  const ordered: Trail[] = [];
  for (let t = best.trail; t !== null; t = t.prev) ordered.push(t);
  ordered.reverse();

  const seenMessage = new Set<string>();
  for (const t of ordered) {
    lines.push({
      kind: 'accommodation',
      pieceId: t.piece.id,
      label: t.piece.label,
      from: arrival + t.startNight,
      nights: t.length,
      price: t.price,
    });
    if (t.piece.kind === 'closure' || t.piece.refuses) {
      // Strongest wins, never last-writer-wins. Assigning outright let the
      // CHRONOLOGICAL order of the tiling decide the answer: a refusing piece
      // on nights 1-2 followed by an on-request closure on night 3 quoted
      // `onRequest`, and the same two rates the other way round quoted
      // `unavailable`. One of those is a stay the park cannot take being
      // offered as merely needing a phone call — `refuses` leaking out as
      // `onRequest` purely because of where it fell in the calendar.
      status = strongest(status, t.piece.onRequest ? 'onRequest' : 'unavailable');
    }
    // A piece that must be confirmed by the park lifts the quote whether or
    // not it also has something to say. This used to live inside the message
    // branch below, which meant `onRequest` with no message quoted as plainly
    // bookable — the flag silently did nothing. Every lifted legacy piece
    // happens to carry both, which is why it went unnoticed; an authored one
    // need not, and a converter emitting the flag alone was being told the
    // stay was instantly confirmable.
    if (t.piece.onRequest) status = strongest(status, 'onRequest');
    if (t.piece.message && !seenMessage.has(t.piece.id)) {
      seenMessage.add(t.piece.id);
      messages.push({
        text: t.piece.message,
        onRequest: Boolean(t.piece.onRequest),
        pieceId: t.piece.id,
      });
    }
  }

  const subtotal = best.cost;
  let total = subtotal;
  const appliedModifiers: AppliedModifier[] = [];
  let rounding: RoundingMode | undefined = set.rounding;

  for (const mod of set.modifiers ?? []) {
    const stayStart = arrival;
    const stayEnd = arrival + N;
    const overlaps = stayStart < mod.window.end && stayEnd > mod.window.start;
    if (!overlaps) continue;
    if (mod.minArrival != null && stayStart < mod.minArrival) continue;
    if (mod.maxDeparture != null && stayEnd > mod.maxDeparture) continue;
    total *= mod.factor;
    appliedModifiers.push({ id: mod.id, label: mod.label, factor: mod.factor });
    rounding = strongestRounding(rounding, mod.rounding);
    if (mod.message) messages.push({ text: mod.message, onRequest: false });
  }

  total = applyRounding(total, rounding);

  return {
    status,
    arrival,
    nights: N,
    lines,
    extras: [],
    extrasTotal: 0,
    accommodationTotal: total,
    subtotal,
    total,
    appliedModifiers,
    messages,
  };
}

/**
 * Insert a label, keeping the dominance rule sound.
 *
 * Unconstrained (minimum already met): keep only the cheapest — provably exact,
 * since cost only grows and future feasibility is identical.
 *
 * Constrained (cost still below an imposed minimum): do NOT collapse to one.
 * The constraint is a LOWER bound, and against a lower bound "cheapest wins"
 * is unsound — a costlier prefix can reach the floor where a cheaper one
 * cannot. QPC collapses here and can therefore miss a cheaper valid tiling;
 * we keep the k cheapest instead (spec §6.1).
 *
 * @returns false if the per-night label cap tripped.
 */
function insert(
  bucket: Map<string, Label[]>, label: Label, kConstrained: number, requiredMin: Money,
  countedSteps: ReadonlySet<string>,
): boolean {
  const key = labelKey(label.cost, requiredMin, label.uses, label.steps, countedSteps);
  const existing = bucket.get(key);

  if (!existing) {
    if (bucket.size >= MAX_LABELS_PER_NIGHT) return false;
    bucket.set(key, [label]);
    return true;
  }

  if (label.cost >= requiredMin) {
    if (label.cost < existing[0].cost) existing[0] = label;
    return true;
  }

  for (const other of existing) if (other.cost === label.cost) return true;
  existing.push(label);
  existing.sort((a, b) => a.cost - b.cost);
  if (existing.length > kConstrained) existing.length = kConstrained;
  return true;
}
